import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo } from 'react'
import { DisplayUeopt } from '../../../constants/enums'
import BadgeAvatar from '../../common/BadgeAvatar'
import FlexGrid from '../../common/FlexGrid'
import Name from '../../common/Name'
import Share from '../../common/Share'
import { getThemeSpacing } from '../../styles/appTheme'
import CounterViewers from '../CounterViewers'
import PreviewPosterImage from '../PreviewPosterImage'

const rootGridStyles = css({
    paddingBottom: getThemeSpacing(1.5),
    paddingTop: getThemeSpacing(2),
    alignItems: 'center',
})

const StyledShareImageDiv = styled('div')({
    marginTop: 3,
    display: 'inline-block',
    padding: getThemeSpacing(0.625),
    background: 'transparent',
})
const StyledName = styled(Name)({
    textAlign: 'center',
    fontSize: '1.2rem',
})

const StyledViewersDiv = styled('div')({
    display: 'flex',
    width: 'calc(100% - 16px)',
    marginLeft: getThemeSpacing(1),
    justifyContent: 'space-between',
    fontSize: '0.75rem',
})

const infoStyles = {
    textAlign: 'center',
    marginTop: getThemeSpacing(2),
} as const

type HeaderProps = {
    onClickAvatar?(): void
    onClickPreview?(): void
    staticImage?: boolean
    item?: boolean
    small?: boolean
    showInfo?: boolean
    broadcasting: boolean
    showViewers?: boolean
    disableBadge?: boolean
    displayMode?: DisplayUeopt
    isMobile?: boolean
    hasShare: boolean
    viewers?: number
    offline: boolean
    is16x9: boolean
    color?: string
    rank?: number
    slug: string
    name: string
    ssr?: boolean
    id: number
}

const Header = memo((props: HeaderProps) => {
    const {
        showInfo = true,
        onClickAvatar = () => {},
        onClickPreview = () => {},
        // sticky = true,
        disableBadge,
        broadcasting,
        showViewers,
        displayMode,
        staticImage,
        isMobile,
        hasShare,
        offline,
        viewers,
        small,
        color,
        slug,
        name,
        ssr,
        id,
    } = props

    const rootStyles = css([
        rootGridStyles,
        {
            ...(small ? { paddingTop: 0, paddingBottom: 0 } : {}),
        },
    ])

    const contentStyles = css({
        justifyContent: 'center',
        gap: getThemeSpacing(small ? 1 : 2),
    })

    const avatarStyleOverrides = css({
        height: !small ? 96 : 81.5625,
        width: !small ? 96 : 81.5625,
        cursor: (onClickAvatar ?? onClickPreview) ? 'pointer' : 'default',
    })

    const Core = (
        <FlexGrid data-testid="RootHeader" overrides={rootStyles}>
            <FlexGrid
                direction="row"
                wrap={small ? 'nowrap' : 'wrap'}
                overrides={contentStyles}
            >
                {(!isMobile || displayMode !== DisplayUeopt.preview) && (
                    <FlexGrid style={{ position: 'relative' }}>
                        <BadgeAvatar
                            css={avatarStyleOverrides}
                            onClick={onClickAvatar ?? onClickPreview}
                            disableBadge={disableBadge}
                            broadcasting={broadcasting}
                            addBoxShadow={true}
                            offline={offline}
                            alt={name}
                            size={small ? 81 : 96}
                            id={id}
                        />
                    </FlexGrid>
                )}
                {(!isMobile || displayMode === DisplayUeopt.preview) && (
                    <FlexGrid>
                        <PreviewPosterImage
                            onClick={onClickPreview}
                            id={id}
                            height={!small ? 95.6256 : 81.5625}
                            width={!small ? 170 : 145}
                            ssr={ssr}
                            staticImage={staticImage}
                            rounded
                        />
                        {showViewers && (
                            <StyledViewersDiv>
                                {typeof viewers === 'number' && !offline && (
                                    <div data-testid="ProfileHeader__Viewers">
                                        <CounterViewers
                                            viewers={viewers}
                                            size={0.6}
                                        />
                                    </div>
                                )}
                            </StyledViewersDiv>
                        )}
                    </FlexGrid>
                )}
            </FlexGrid>
            {showInfo && (
                <FlexGrid style={infoStyles}>
                    <StyledName color={color} text={name} />
                    {hasShare && (
                        <StyledShareImageDiv>
                            <Share slug={slug} width={80} />
                        </StyledShareImageDiv>
                    )}
                </FlexGrid>
            )}
        </FlexGrid>
    )

    return Core
})

export default Header
